import React, { useState, useEffect } from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import StudentHome from "../student/studentPage.jsx";
import { useUser } from "../../contexts/UserContext";
import { storageService } from '../../services/storage';
import { courseService, userService } from '../../services/api';
import { ROUTES, USER_ROLES } from '../../utils/constants';
import { useNavigate } from 'react-router-dom';
import '../../styles/components/pages/auth.css';

export default function StudentAuthentication() {
    const [gtid, setGtid] = useState("");
    const [passcode, setPasscode] = useState("");
    const [confirmPasscode, setConfirmPasscode] = useState("");
    const [isSignUp, setIsSignUp] = useState(false);
    const [consentGiven, setConsentGiven] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState();
    const loggedIn = false;
    const { setUser, setRoleData } = useUser();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await courseService.getCoursesInProgress();
                console.log("Courses in progress:", response);
                setCourses(response);
            } catch (error) {
                console.error("Error fetching courses in progress:", error);
            }
        };

        fetchCourses();
    }, []);

    const handleNoConsent = () => {
        alert("You do not have access to this platform without consent.");
    };

    const validateFields = () => {
        const newErrors = {};

        // Validate GTID
        if (!gtid || gtid.length !== 9 || !/^\d{9}$/.test(gtid)) {
            newErrors.gtid = "Your GTID must be a 9-digit number.";
        }

        if (isSignUp) {
            // Validate Passcode
            if (!passcode || passcode.length < 4 || passcode.length > 6 || !/^\d{4,6}$/.test(passcode)) {
                newErrors.passcode = "Your passcode must be a 4 to 6 digit number.";
            }

            // Validate Confirm Passcode
            if (passcode !== confirmPasscode) {
                newErrors.confirmPasscode = "Your passcodes do not match.";
            }

            // Validate Course
            if (!selectedCourse) {
                newErrors.selectedCourse = "You must select a course.";
            }

        } else {
            // Validate Passcode for Login
            if (!passcode || passcode.length < 4 || passcode.length > 6 || !/^\d{4,6}$/.test(passcode)) {
                newErrors.passcode = "Your passcode must be a 4 to 6 digit number.";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLogin = async () => {
        if (!validateFields()) return;

        setIsLoading(true);
        try {
            // Send login request to the backend
            const response = await userService.loginStudent({
                gtid: parseInt(gtid, 10),
                password: passcode,
            });

            // Extract student data from response
            const { student } = response;

            // Temporary TA check - replace these IDs with actual TA GTIDs
            const taGTIDs = ['903904776', '100000011', '100000012', '100000001', 'ta_test']; // Add actual TA GTIDs here
            const isTA = taGTIDs.includes(gtid.toString());

            // Set common login data
            storageService.setLoggedIn(true);
            storageService.setLoginId(gtid);
            storageService.setStudentId(student.studentID);
            setUser(student);
            // Login successful, student data and StudentID are set in localStorage.

            if (isTA) {
                // Handle TA login
                setRoleData(USER_ROLES.INSTRUCTOR);
                // Small delay to ensure state propagates
                setTimeout(() => navigate('/instructor'), 0);
            } else {
                // Handle student login
                setRoleData(USER_ROLES.STUDENT);
                // Small delay to ensure state propagates
                setTimeout(() => navigate('/dashboard'), 0);
            }
        } catch (error) {
            console.error("Login error:", error);
            alert(error.message || "Login failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleSignup = async () => {
        if (!validateFields()) return;
    
        setIsLoading(true);
        try {
            console.log(selectedCourse);
            // Send signup request to the backend
            const response = await userService.registerStudent({
                gtid: parseInt(gtid, 10),
                password: passcode,
                confirmPassword: confirmPasscode,
                origin: "web",
                course: selectedCourse
            });
    
            // Handle successful signup
            const { studentID, username } = response;
    
            // Save studentID and username in localStorage
            storageService.setStudentId(studentID);
            storageService.setUserCode(username);
    
            alert("Signup successful! You can now log in.");
            navigate('/studentAuth'); // Redirect to login page
        } catch (error) {
            console.error("Signup error:", error);
            alert(error.message || "Signup failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const renderForm = () => (
        <Form className="auth-form">
            <Form.Group className="auth-form-group" controlId="formGtid">
                <Form.Label>Your GTID:</Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Enter your GTID"
                    value={gtid}
                    onChange={(e) => setGtid(e.target.value)}
                    isInvalid={!!errors.gtid}
                />
                <Form.Control.Feedback type="invalid">{errors.gtid}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="auth-form-group" controlId="formPasscode">
                <Form.Label>{isSignUp ? "New 4 to 6 Digit Passcode:" : "Passcode:"}</Form.Label>
                <Form.Control
                    type="password"
                    placeholder={isSignUp ? "Enter a new passcode" : "Enter your passcode"}
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    isInvalid={!!errors.passcode}
                />
                <Form.Control.Feedback type="invalid">{errors.passcode}</Form.Control.Feedback>
            </Form.Group>

            {isSignUp && (
                <Form.Group className="auth-form-group" controlId="formConfirmPasscode">
                    <Form.Label>Re-Enter Passcode:</Form.Label>
                    <Form.Control
                        type="password"
                        placeholder="Re-enter your passcode"
                        value={confirmPasscode}
                        onChange={(e) => setConfirmPasscode(e.target.value)}
                        isInvalid={!!errors.confirmPasscode}
                    />
                    <Form.Control.Feedback type="invalid">{errors.confirmPasscode}</Form.Control.Feedback>
                </Form.Group>
            )}

           {isSignUp && (
                <Form.Group className="auth-form-group" controlId="formCourse">
                    <Form.Label>Your Course:</Form.Label>
                    <Form.Control
                        as="select"
                        value={selectedCourse}
                        onChange={(e) => setSelectedCourse(e.target.value)}
                        isInvalid={!!errors.selectedCourse}
                    >
                        <option value="">Select your course</option>
                        {courses.map((course, index) => (
                            <option key={index} value={course.CRN}>
                                {course.name}: {course.semester}
                            </option>
                        ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">{errors.selectedCourse}</Form.Control.Feedback>
                </Form.Group>
            )}

            <Button
                className="auth-button"
                variant="primary"
                onClick={isSignUp ? handleSignup : handleLogin}
                disabled={isLoading}
            >
                {isLoading ? (isSignUp ? "Signing up..." : "Logging in...") : isSignUp ? "Sign Up" : "Log In"}
            </Button>
            <p className="auth-toggle-text">
                {isSignUp ? (
                    <>
                        Already have an account?{" "}
                        <span onClick={() => setIsSignUp(false)} className="auth-toggle-link">
                            Log in
                        </span>
                    </>
                ) : (
                    <>
                        Don't have an account?{" "}
                        <span onClick={() => setIsSignUp(true)} className="auth-toggle-link">
                            Sign up
                        </span>
                    </>
                )}
            </p>
        </Form>
    );

    return (
        <div className="auth-container">
            {!isSignUp && (
                <button className="auth-back-button" onClick={() => navigate('/')}>
                    ← Back To Homepage
                </button>
            )}
            {isSignUp && !consentGiven && (
                <button className="auth-back-button" onClick={() => setIsSignUp(false)}>
                    ← Back To Sign-In
                </button>
            )}
            {loggedIn ? (
                <StudentHome />
            ) : (
                <>
                    <h3 className="auth-header">
                        {isSignUp
                            ? consentGiven
                                ? "Create an Account"
                                : "Consent to Record Submission"
                            : "Log in to Your Account"}
                    </h3>
                    {isSignUp && !consentGiven ? (
                        <>
                            <div className="auth-consent">
                                <div className="auth-consent-text">
                                    <p>
                                        You are being asked to volunteer in a research study aimed at improving learning outcomes in computer science courses through the use of an AI-powered learning environment. The purpose of this study is to assess typical challenges faced by novice coders and explore how AI-driven tools can enhance learning.
                                    </p>

                                    <p>
                                        To participate in this study, you must be enrolled in the CS 1301 course and physically located in the United States at the time of participation. Only students meeting these criteria are eligible to participate.
                                    </p>

                                    <p>
                                        Participation in the study involves using the learning environment to complete coding and tracing problems, which you may do at your own pace throughout the semester. You may also provide optional feedback on the relevance and helpfulness of the questions and recommended guidance. Your total time on the learning environment is entirely up to you, based on how much practice you choose to complete.
                                    </p>

                                    <p>
                                        All data collected from your participation, including coding submissions and feedback, will be de-identified before being used for research. While your name and login may be visible to your instructor, this identifying information will not be accessible to the research team. Instead, your data will be linked to a randomly generated, encrypted participant ID that cannot be used by the researchers to identify you. Data will be securely stored in an AWS database with industry-standard protections, including encryption and access control. Only authorized members of the research team will have access to the de-identified data.
                                    </p>

                                    <p>
                                        The risks involved in this study are no greater than those encountered in daily activities. While there are no direct personal benefits, you may receive extra credit for participating. Specifically, you will earn 0.2 points of extra credit for each completed problem set (e.g., homework assignment) within the learning environment, up to a maximum of 1.0 point added to your final numeric grade in the course. An alternative assignment offering equivalent extra credit will be available for students who choose not to participate, ensuring equal opportunity. Your decision to participate or not will not otherwise affect your standing or performance in the course.
                                    </p>

                                    <p>
                                        We will comply with all applicable laws and regulations regarding confidentiality. To ensure the research is conducted properly, study records may be reviewed by the Georgia Institute of Technology Institutional Review Board (IRB) or the Office for Human Research Protections (OHRP).
                                    </p>

                                    <p>
                                        If you have any questions about the study, you may contact Rodrigo Borela (PI) at rborelav@gatech.edu or Nimisha Roy (co-PI) at nroy9@gatech.edu. If you have questions about your rights as a research subject, you may contact the Georgia Institute of Technology Office of Research Integrity Assurance at IRB@gatech.edu.
                                    </p>

                                    <p>
                                        Thank you for considering participating in this study.
                                    </p>

                                    <p>
                                        By clicking "I consent," you indicate your consent to participate in the study.
                                    </p>
                                </div>
                                <div className="auth-consent-buttons">
                                    <Button
                                        className="auth-button"
                                        variant="primary"
                                        onClick={() => setConsentGiven(true)}
                                    >
                                        I Consent
                                    </Button>
                                    <Button
                                        className="auth-button auth-button-danger"
                                        variant="danger"
                                        onClick={handleNoConsent}
                                    >
                                        I Do Not Consent
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        renderForm()
                    )}
                </>
            )}
        </div>
    );
}